import React, { useEffect, useState } from "react";
import { Form, Table } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate, useParams, useRouteLoaderData } from "react-router-dom";

import { useAppApi } from "@/App";
import { DEVELOPER_DEFAULT_ID } from "@/const";
import { DevForm } from "@components/Forms/Fields";
import { useSubmit } from "@components/Forms/Handlers";
import { DefaultLayout } from "@components/Layouts";
import { CommonCard } from "@components/Pages";
import { url_reverse } from "@components/Utils";

export default function GameCreateScreen({ screen }) {
    return <DefaultLayout>{screen === "create" ? <GameCreate /> : <DeveloperChoose />}</DefaultLayout>;
}

function GameCreate() {
    const { t } = useTranslation();
    const params = useParams();
    const { developerId } = params;
    const { apiData } = useAppApi(url_reverse("api:game:create", { developer_id: developerId }));
    const onFormSubmit = useSubmit();
    const [gameType, setGameType] = useState({});
    const navigate = useNavigate();
    const commonInfo = useRouteLoaderData("commonInfo");

    useEffect(() => {
        if (apiData?.forms_data) {
            if (apiData.forms_data.main.data.main_type === "client") {
                setGameType("client");
                apiData.forms_data.main.data.audience = "";
                apiData.forms_data.main.data.publication_platform = "vkplay";
            }
            if (apiData.forms_data.main.data.main_type === "browser") {
                setGameType("browser");
                apiData.forms_data.main.data.type = "";
            }
        }
    }, [apiData]);

    useEffect(() => {
        if (
            apiData?.forms_data.main.data.developer &&
            apiData?.forms_data.main.data.developer.toString() !== developerId.toString()
        ) {
            navigate(url_reverse("games:create", { developer_id: apiData?.forms_data.main.data.developer }), {
                replace: true,
            });
        }
    }, [apiData?.forms_data.main.data.developer]);

    if (!apiData) return;

    const header = t("Создание проекта");

    const onOK = (resp) => {
        window.location = url_reverse(commonInfo.account.is_super === true ? "games:admin" : "games:locale:edit", {
            game_id: resp.game.id,
            locale: resp.game.base_locale,
        });
    };

    return (
        <CommonCard header={header}>
            <Form onSubmit={(ev) => onFormSubmit(ev, { onOK })}>
                {commonInfo.account.is_super === true && <GameCreateSuper gameType={gameType} />}
                {commonInfo.account.is_super === false && <GameCreateDeveloper gameType={gameType} />}
                <div className="row mt-3">
                    <div className="col-sm-3" />
                    <div className="col-sm-9">
                        <DevForm.Button variant="primary" type="submit" name="create">
                            {t("Создать")}
                        </DevForm.Button>
                    </div>
                </div>
            </Form>
        </CommonCard>
    );
}

function GameCreateSuper({ gameType }) {
    return (
        <>
            <DevForm.Field prefix="main" name="developer" />
            <DevForm.Field prefix="locale" name="name" />
            <DevForm.Field prefix="main" name="main_type" />
            {gameType === "client" && <DevForm.Field prefix="main" name="type" />}
            {gameType === "browser" && (
                <div className="form-group row">
                    <div className="my-auto col-sm-3"></div>
                    <div className="my-auto col-sm-9">
                        <Trans>
                            <p className="font-weight-bold">Внимание разработчикам браузерных игр!</p>
                        </Trans>
                        <Trans>
                            <p>
                                С 1 декабря 2024 года мы прекратили размещение казуальных игр на{" "}
                                <a href="https://mini.vkplay.ru/" target="_blank">
                                    Mini VK Play
                                </a>
                                . С 1 января 2025 года витрина будет полностью закрыта. Браузерные игры будут
                                приниматься к публикации на{" "}
                                <a href="https://vkplay.ru/" target="_blank">
                                    VK Play
                                </a>
                                .
                            </p>
                        </Trans>

                        <p>
                            <Trans>
                                Обратите внимание: браузерные игры не должны содержать встроенной рекламной монетизации.
                                В случае обнаружения рекламных интеграций в проекте публикация будет отклонена.
                            </Trans>
                        </p>

                        <Trans>
                            <p>
                                Для игр с рекламной монетизацией рекомендуем рассмотреть альтернативные площадки
                                размещения, такие как{" "}
                                <a href="https://vk.com/games/genres/arcades" target="_blank">
                                    ВКонтакте
                                </a>{" "}
                                или{" "}
                                <a href="https://ok.ru/vitrine/casual" target="_blank">
                                    Одноклассники
                                </a>
                                .
                            </p>
                        </Trans>
                    </div>
                </div>
            )}
            <DevForm.Field prefix="main" name="supported_os" />
            <DevForm.Field prefix="main" name="base_locale" />
            <DevForm.Field prefix="main" name="clone_game" />

            {gameType === "client" && <DevForm.Field prefix="main" name="gc_server_type" />}
            <DevForm.Field prefix="locale" name="create_souz" />
            <DevForm.Field prefix="locale" name="create_sezam" />
        </>
    );
}

function GameCreateDeveloper({ gameType }) {
    const { t } = useTranslation();
    return (
        <>
            <DevForm.Field prefix="locale" name="name" />
            <DevForm.Field prefix="main" name="main_type" />
            {gameType === "browser" && (
                <>
                    <div className="form-group row">
                        <div className="my-auto col-sm-3"></div>
                        <div className="my-auto col-sm-9">
                            <Trans>
                                <p className="font-weight-bold">Внимание разработчикам браузерных игр!</p>
                            </Trans>
                            <Trans>
                                <p>
                                    С 1 декабря 2024 года мы прекратили размещение казуальных игр на{" "}
                                    <a href="https://mini.vkplay.ru/" target="_blank">
                                        Mini VK Play
                                    </a>
                                    . С 1 января 2025 года витрина будет полностью закрыта. Браузерные игры будут
                                    приниматься к публикации на{" "}
                                    <a href="https://vkplay.ru/" target="_blank">
                                        VK Play
                                    </a>
                                    .
                                </p>
                            </Trans>

                            <p>
                                <Trans>
                                    Обратите внимание: браузерные игры не должны содержать встроенной рекламной
                                    монетизации. В случае обнаружения рекламных интеграций в проекте публикация будет
                                    отклонена.
                                </Trans>
                            </p>

                            <Trans>
                                <p>
                                    Для игр с рекламной монетизацией рекомендуем рассмотреть альтернативные площадки
                                    размещения, такие как{" "}
                                    <a href="https://vk.com/games/genres/arcades" target="_blank">
                                        ВКонтакте
                                    </a>{" "}
                                    или{" "}
                                    <a href="https://ok.ru/vitrine/casual" target="_blank">
                                        Одноклассники
                                    </a>
                                    .
                                </p>
                            </Trans>
                        </div>
                    </div>
                    <DevForm.Field prefix="main" name="supported_os" />
                </>
            )}
            <DevForm.Field prefix="main" name="base_locale" />
            <div className="row mt-3">
                <div className="col-sm-3" />
                <div className="col-sm-9">
                    <a href="https://documentation.vkplay.ru/f2p_vkp/f2p_create_vkp" target="_blank">
                        {t("Как создать проект")}
                    </a>
                </div>
            </div>
        </>
    );
}

function DeveloperChoose() {
    const { t } = useTranslation();
    const { apiData } = useAppApi(url_reverse("api:developer:choose"));
    const commonInfo = useRouteLoaderData("commonInfo");
    const navigate = useNavigate();

    useEffect(() => {
        if (apiData && commonInfo.account.is_super === true) {
            navigate(url_reverse("games:create", { developer_id: DEVELOPER_DEFAULT_ID }), { replace: true });
        }
        if (apiData?.object_list?.length === 1) {
            navigate(url_reverse("games:create", { developer_id: apiData.object_list[0].id }), { replace: true });
        }
    }, [apiData]);

    if (!apiData) return;

    return (
        !commonInfo.account.is_super && (
            <CommonCard header={t("Выберите разработчика")}>
                <Table hover responsive className="dmg-table">
                    <thead>
                        <tr>
                            <th>{t("Разработчик")}</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {apiData.object_list?.map((o, i) => {
                            return (
                                <tr key={`object_list${i}`}>
                                    <td>{o.name}</td>
                                    <td className="dmg-table-row-tools">
                                        <Link to={url_reverse("games:create", { developer_id: o.id })}>
                                            {t("Создать игру")}
                                        </Link>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </CommonCard>
        )
    );
}
